/**
 * A CCTV clip, played inside HR.
 *
 * The problem this solves: a `<video>` element fetches the file itself and
 * sends no Authorization header, so the clip cannot simply be pointed at a
 * bearer-protected route. The two usual workarounds are both wrong for
 * evidence — a signed URL or a token in the query string is a link that can be
 * forwarded, which is exactly what this whole feature exists to remove.
 *
 * So: an authenticated POST to `…/evidence/{n}/watch` performs the permission
 * check, records the view in the audit log once, and sets an HttpOnly,
 * SameSite=Strict cookie naming this one file for 15 minutes. Only then is the
 * player pointed at `…/evidence/{n}/stream`, which serves byte ranges — so
 * playback starts at once and scrubbing does not re-download the clip.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AlertCircle, Loader2 } from 'lucide-react';

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const prettySize = (bytes) => {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
};

export const QCEvidenceVideo = ({ violation, index, title, token, isRTL = false, onClose }) => {
  const [status, setStatus] = useState('opening');
  const [info, setInfo] = useState(null);
  const [message, setMessage] = useState('');
  const videoRef = useRef(null);
  const t = (en, ar) => (isRTL ? ar : en);

  const streamUrl = `${API_URL}/qc-violations/${violation.id}/evidence/${index}/stream`;
  // The cookie only travels on its own if the player's request is same-origin;
  // when the API is on another host the element has to ask for credentials.
  const sameOrigin =
    typeof window !== 'undefined' && API_URL.startsWith(window.location.origin);

  useEffect(() => {
    let alive = true;
    setStatus('opening');
    axios
      .post(
        `${API_URL}/qc-violations/${violation.id}/evidence/${index}/watch`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      )
      .then((res) => {
        if (!alive) return;
        setInfo(res.data);
        setStatus('ready');
      })
      .catch((err) => {
        if (!alive) return;
        const detail = err?.response?.data?.detail;
        setMessage(
          (isRTL ? detail?.error_ar : detail?.error) ||
            detail?.error ||
            t('This clip could not be opened.', 'لا يمكن فتح هذا المقطع.')
        );
        setStatus('error');
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [violation.id, index, token]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-4xl w-[95vw] p-0 gap-0 overflow-hidden"
        data-testid="qc-evidence-video-dialog"
      >
        <div className="px-4 py-3 border-b border-border/60 flex items-baseline gap-2">
          <DialogTitle className="text-base">
            {title || t('Violation evidence', 'دليل المخالفة')}
          </DialogTitle>
          {info?.size ? (
            <span className="text-xs text-slate-500 tabular-nums">{prettySize(info.size)}</span>
          ) : null}
        </div>

        {status === 'opening' && (
          <div
            className="h-64 flex items-center justify-center gap-2 text-sm text-slate-500"
            data-testid="qc-evidence-video-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('Opening the clip…', 'جارٍ فتح المقطع…')}
          </div>
        )}

        {status === 'error' && (
          <div
            className="h-64 flex flex-col items-center justify-center gap-2 px-6 text-center"
            data-testid="qc-evidence-video-error"
          >
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <p className="text-sm text-slate-600">{message}</p>
          </div>
        )}

        {status === 'ready' && (
          <div className="bg-black">
            <video
              ref={videoRef}
              src={streamUrl}
              controls
              autoPlay
              playsInline
              preload="metadata"
              controlsList="nodownload"
              crossOrigin={sameOrigin ? undefined : 'use-credentials'}
              className="w-full max-h-[70vh] bg-black"
              data-testid="qc-evidence-video-player"
            />
          </div>
        )}

        {info?.file_name && (
          <div className="px-4 py-2 border-t border-border/60 text-xs text-slate-500 truncate">
            {t('File', 'الملف')}: {info.file_name}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QCEvidenceVideo;
